// US2017758 Bucket A — i18next i18n module (AD-5).
//
// Localization mechanism for the Support Button: i18next over STATIC IN-BUNDLE
// per-locale JSON resources. No runtime fetch / no i18next-http-backend (Hard
// Rule #1 intent), and no i18next browser language detection — the active `lng`
// is ALWAYS driven explicitly from the host `bb_lang` attribute via
// `normalizeLocale` (Hard Rule #4: no navigator.language / window / document).
//
// A DEDICATED instance (createInstance) is used so we never mutate a host page's
// global i18next singleton. Init is synchronous (`initImmediate: false`) so
// `lookupLabel` is usable in the same tick — no async in the Lit render path and
// no awaiting in happy-dom unit tests.

import i18next from "i18next";
import { normalizeLocale } from "../utils/normalize-locale.js";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import frCA from "./locales/fr-CA.json";
import es from "./locales/es.json";

// Single source of truth for the recognized locales. App.ts imports this to
// decide whether a normalized `bb_lang` is recognized (else → "en" + warn).
export const SUPPORTED_LOCALES = ["en", "fr", "fr-CA", "es"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

// The Support Button label key inside the "default" namespace.
export const SUPPORT_BUTTON_LABEL_KEY = "supportButton.label";

const i18n = i18next.createInstance();

// Synchronous init — resources are inline, so `initImmediate: false` loads them in
// the same tick and `lookupLabel` is usable immediately (no async in the Lit render
// path, no awaiting in happy-dom unit tests). The returned promise resolves
// synchronously and is intentionally not chained. `load: "currentOnly"` + explicit
// `supportedLngs` keep `fr-CA` FIRST-CLASS (its own strings, never falls back to
// `fr`); `fallbackLng: "en"` is the SOLE fallback.
i18n.init({
  initImmediate: false,
  defaultNS: "default",
  ns: ["default"],
  fallbackLng: "en",
  supportedLngs: [...SUPPORTED_LOCALES],
  load: "currentOnly",
  nonExplicitSupportedLngs: false,
  returnNull: false,
  resources: {
    en,
    fr,
    "fr-CA": frCA,
    es,
  },
});

// Resolve a translated label. This is the PUBLIC, normalization-OWNING entry point
// for label lookup: `locale` MAY be a raw host token ("FR", "fr_CA", " es ") OR an
// already-canonical SupportedLocale — it is normalized here (case/separator-tolerant)
// and passed to i18next as an explicit `lng`. Unknown/empty locale → English
// (fallbackLng). Unknown key → i18next returns the key itself (never undefined /
// never throws), satisfying the safe-default contract.
//
// Normalization ownership (ARGUS PR #153 [Low] — the two normalize sites are by design,
// NOT ambiguous): App.ts._resolveLocale() also calls normalizeLocale, but for a DIFFERENT
// concern — deciding whether bb_lang is a RECOGNIZED locale (else "en" + a one-time warn)
// and what to expose as the trigger's `.locale`. It then passes the already-canonical
// `_resolvedLocale` here, where this normalize is a guaranteed no-op. So: recognition +
// warn live in App.ts; label-string normalization lives here. Keep lookupLabel TOLERANT —
// its Catalog tests pass raw "FR"/"fr-ca"/"FR_CA" directly — do NOT make it strict.
export function lookupLabel(key: string, locale: string): string {
  const lng = normalizeLocale(locale) || "en";
  const value = i18n.t(key, { lng });
  return typeof value === "string" ? value : key;
}
